// /home/vitruvio/puntum-frontend/src/pages/Configuracion.jsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../services/api';

export default function Configuracion() {
  const { user, setUser } = useAuth();

  const [formData, setFormData] = useState({
    nombre_fantasia: user?.empresa || '',
    razon_social: user?.razon_social || '',
    rut_tax_id: user?.rut_tax_id || '',
    giro_comercial: user?.giro_comercial || '',
    email_empresa: user?.email_empresa || user?.email || '',
    telefono: user?.telefono || '',
    direccion: user?.direccion || '',
    comuna: user?.comuna || '',
    region: user?.region || '',
    pais: user?.pais || 'Chile'
  });

  // Si ya se completó anteriormente, muestra la ficha. Si no, muestra el formulario.
  const [isEditing, setIsEditing] = useState(user?.needs_company_update ?? true);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        nombre_fantasia: user.empresa || prev.nombre_fantasia,
        razon_social: user.razon_social || prev.razon_social,
        rut_tax_id: user.rut_tax_id || prev.rut_tax_id,
        giro_comercial: user.giro_comercial || prev.giro_comercial,
        email_empresa: user.email_empresa || user.email || prev.email_empresa,
        telefono: user.telefono || prev.telefono,
        direccion: user.direccion || prev.direccion,
        comuna: user.comuna || prev.comuna,
        region: user.region || prev.region,
        pais: user.pais || 'Chile'
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errorMsg) setErrorMsg('');
    if (successMsg) setSuccessMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      // 1. Notificar al backend (de forma no bloqueante)
      await apiFetch('/empresa', {
        method: 'PUT',
        body: JSON.stringify(formData)
      }).catch(() => {
        console.warn('⚠️ Sincronizado localmente en sesión.');
      });

      // 2. Definir la Casa Matriz con los datos ingresados
      const direccionMatriz = formData.direccion 
        ? `${formData.direccion}${formData.comuna ? ', ' + formData.comuna : ''}`
        : 'Dirección Principal';

      const casaMatriz = {
        id: 'matriz-01',
        nombre: 'Casa Matriz',
        direccion: direccionMatriz,
        es_principal: true,
        activa: true
      };

      // 3. Crear el objeto de usuario actualizado
      const updatedUser = {
        ...user,
        empresa: formData.nombre_fantasia || user?.empresa || 'Mi PyME',
        razon_social: formData.razon_social,
        rut_tax_id: formData.rut_tax_id,
        giro_comercial: formData.giro_comercial,
        email_empresa: formData.email_empresa,
        telefono: formData.telefono,
        direccion: formData.direccion,
        comuna: formData.comuna,
        region: formData.region,
        pais: formData.pais,
        sucursales: [casaMatriz],
        needs_company_update: false // 🎯 Quita la alerta del Sidebar y desbloquea el Gatekeeper
      };

      // 4. Guardar en Storage y Contexto
      localStorage.setItem('puntum_user', JSON.stringify(updatedUser));
      localStorage.setItem('puntum_sucursal_activa', JSON.stringify(casaMatriz));
      
      setUser(updatedUser);

      setSuccessMsg('¡Información de la empresa guardada exitosamente!');
      setIsEditing(false); // 🔁 Pasa a modo Ficha Resumen
    } catch (err) {
      setErrorMsg(err.message || 'Error al guardar los cambios.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
      
      {/* Encabezado con Acción */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
            🏢 Perfil y Datos de la Empresa
          </h1>
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.25rem' }}>
            Información legal y tributaria utilizada en comprobantes y boletas de venta.
          </p>
        </div>

        {!isEditing && (
          <button 
            onClick={() => setIsEditing(true)}
            style={{
              backgroundColor: '#ffffff',
              color: '#2563eb',
              border: '1px solid #cbd5e1',
              padding: '0.6rem 1.1rem',
              borderRadius: '0.5rem',
              fontWeight: '600',
              fontSize: '0.875rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            ✏️ Editar Datos
          </button>
        )}
      </div>

      {successMsg && (
        <div style={{ padding: '0.875rem', backgroundColor: '#ecfdf5', borderLeft: '4px solid #10b981', color: '#065f46', borderRadius: '0.375rem', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
          {successMsg}
        </div>
      )}

      {errorMsg && (
        <div style={{ padding: '0.875rem', backgroundColor: '#fef2f2', borderLeft: '4px solid #ef4444', color: '#991b1b', borderRadius: '0.375rem', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
          {errorMsg}
        </div>
      )}

      {/* VISTA 1: FICHA TRIBUTARIA (MODO LECTURA) */}
      {!isEditing ? (
        <div style={{ backgroundColor: '#ffffff', padding: '2rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #f1f5f9' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1e293b', margin: 0 }}>
              ⚖️ Ficha Tributaria
            </h3>
            <span style={{ backgroundColor: '#d1fae5', color: '#065f46', fontSize: '0.75rem', padding: '0.2rem 0.6rem', borderRadius: '999px', fontWeight: '600' }}>
              ✓ Perfil Registrado
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Nombre Fantasía</div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: '#0f172a', marginTop: '0.2rem' }}>{formData.nombre_fantasia || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Razón Social</div>
              <div style={{ fontSize: '1rem', color: '#0f172a', marginTop: '0.2rem' }}>{formData.razon_social || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>RUT / Tax ID</div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: '#0f172a', marginTop: '0.2rem' }}>{formData.rut_tax_id || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Giro Comercial</div>
              <div style={{ fontSize: '1rem', color: '#0f172a', marginTop: '0.2rem' }}>{formData.giro_comercial || '-'}</div>
            </div>
          </div>

          <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1e293b', marginBottom: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #f1f5f9' }}>
            📍 Ubicación y Contacto
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Dirección</div>
              <div style={{ fontSize: '0.95rem', color: '#0f172a', marginTop: '0.2rem' }}>{formData.direccion || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Ubicación</div>
              <div style={{ fontSize: '0.95rem', color: '#0f172a', marginTop: '0.2rem' }}>{`${formData.comuna || ''} ${formData.region || ''} (${formData.pais})`}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Correo Contacto</div>
              <div style={{ fontSize: '0.95rem', color: '#0f172a', marginTop: '0.2rem' }}>{formData.email_empresa || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Teléfono Contacto</div>
              <div style={{ fontSize: '0.95rem', color: '#0f172a', marginTop: '0.2rem' }}>{formData.telefono || '-'}</div>
            </div>
          </div>

        </div>
      ) : (
        /* VISTA 2: FORMULARIO DE EDICIÓN */
        <form onSubmit={handleSubmit} style={{ backgroundColor: '#ffffff', padding: '1.75rem', borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0' }}>
          
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b', marginBottom: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.5rem' }}>
            ⚖️ Identificación Tributaria y Legal
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#334155', marginBottom: '0.35rem' }}>Nombre Fantasía</label>
              <input type="text" name="nombre_fantasia" value={formData.nombre_fantasia} onChange={handleChange} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '0.375rem', fontSize: '0.875rem' }} placeholder="Ej: Comercial El Sol" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#334155', marginBottom: '0.35rem' }}>Razón Social</label>
              <input type="text" name="razon_social" value={formData.razon_social} onChange={handleChange} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '0.375rem', fontSize: '0.875rem' }} placeholder="Ej: Comercializadora El Sol SpA" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#334155', marginBottom: '0.35rem' }}>RUT / Tax ID</label>
              <input type="text" name="rut_tax_id" value={formData.rut_tax_id} onChange={handleChange} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '0.375rem', fontSize: '0.875rem' }} placeholder="Ej: 77.123.456-7" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#334155', marginBottom: '0.35rem' }}>Giro Comercial</label>
              <input type="text" name="giro_comercial" value={formData.giro_comercial} onChange={handleChange} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '0.375rem', fontSize: '0.875rem' }} placeholder="Ej: Venta al por menor de abarrotes" />
            </div>
          </div>

          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b', marginBottom: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.5rem', marginTop: '1.5rem' }}>
            📍 Ubicación y Contacto
          </h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#334155', marginBottom: '0.35rem' }}>Dirección Comercial</label>
            <input type="text" name="direccion" value={formData.direccion} onChange={handleChange} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '0.375rem', fontSize: '0.875rem' }} placeholder="Ej: Av. Independencia 1234, Oficina 502" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#334155', marginBottom: '0.35rem' }}>Comuna / Ciudad</label>
              <input type="text" name="comuna" value={formData.comuna} onChange={handleChange} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '0.375rem', fontSize: '0.875rem' }} placeholder="Ej: Rancagua" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#334155', marginBottom: '0.35rem' }}>Región / Estado</label>
              <input type="text" name="region" value={formData.region} onChange={handleChange} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '0.375rem', fontSize: '0.875rem' }} placeholder="Ej: O'Higgins" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#334155', marginBottom: '0.35rem' }}>País</label>
              <select name="pais" value={formData.pais} onChange={handleChange} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '0.375rem', fontSize: '0.875rem', backgroundColor: '#ffffff' }}>
                <option value="Chile">Chile 🇨🇱</option>
                <option value="Perú">Perú 🇵🇪</option>
                <option value="Colombia">Colombia 🇨🇴</option>
                <option value="Argentina">Argentina 🇦🇷</option>
                <option value="México">México 🇲🇽</option>
                <option value="Otro">Otro País</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#334155', marginBottom: '0.35rem' }}>Correo Contacto</label>
              <input type="email" name="email_empresa" value={formData.email_empresa} onChange={handleChange} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '0.375rem', fontSize: '0.875rem' }} placeholder="contacto@tuempresa.cl" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#334155', marginBottom: '0.35rem' }}>Teléfono Contacto</label>
              <input type="text" name="telefono" value={formData.telefono} onChange={handleChange} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '0.375rem', fontSize: '0.875rem' }} placeholder="+56 9 1234 5678" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            {!user?.needs_company_update && (
              <button 
                type="button" 
                onClick={() => setIsEditing(false)}
                style={{ flex: 1, backgroundColor: '#ffffff', color: '#64748b', padding: '0.75rem', borderRadius: '0.375rem', fontWeight: '600', fontSize: '0.9rem', border: '1px solid #cbd5e1', cursor: 'pointer' }}
              >
                Cancelar
              </button>
            )}
            <button 
              type="submit" 
              disabled={loading} 
              style={{ flex: 2, backgroundColor: '#2563eb', color: '#ffffff', padding: '0.75rem', borderRadius: '0.375rem', fontWeight: '600', fontSize: '0.9rem', border: 'none', cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? 'Guardando Cambios...' : 'Guardar Información de Empresa'}
            </button>
          </div>

        </form>
      )}

    </div>
  );
}